import React, { useEffect, useState } from "react";
import { Box, Drawer, IconButton, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import { connect } from "react-redux";

import DefaultVehicleSettingsCard from "./DefaultVehicleSettingsCard";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import API from "../../../utils/axios";

import DefaultRouteOptimizationSettingsCard from "./DefaultRouteOptimizationSettingsCard";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { secondsConvert } from "../../../utils/helpers";
import TitleWithEducationalMaterials from "../../common/TitleWithEducationalMaterials";

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: "1302 !important"
  },
  paper: {
    maxWidth: "100%"
  },
  contentWrapper: {
    minWidth: 600,
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    maxWidth: 904,
    background: "#F7F7F7",
    minHeight: "100%",
    overflow: "hidden"
  },
  header: {
    boxSizing: "border-box",
    border: "1px solid #eee",
    backgroundColor: "#FFFFFF"
  },
  closeButton: {
    marginRight: theme.spacing(2)
  }
}));

function RouteSetupDialog(props) {
  const { open, drawerOpen, handleClose, setSaveDisabled, saveDisabled } =
    props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  // States
  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    action: () => {}
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [vehicleSettings, setVehicleSettings] = useState(null);
  const [routingSettings, setRoutingSettings] = useState(null);

  // Callbacks
  const warnClose = saveDisabled
    ? handleClose
    : () => setChangesDialog({ visible: true, action: handleClose });

  // Effects
  useEffect(() => {
    if (open) {
      setSaveDisabled(true);
    }
  }, [open, setSaveDisabled]);

  useEffect(() => {
    if (open) {
      setLoadingSettings(true);
      Promise.all([
        API.get("/v1/settings/vehicles"),
        API.get("/v1/settings/projects")
      ])
        .then(settingsData => {
          const [
            { data: vehiclesSettingsFromBE },
            { data: routingSettingsFromBE }
          ] = settingsData;
          const theVehicleSettings = {
            startLocation: vehiclesSettingsFromBE.start_address,
            startTime: secondsConvert(vehiclesSettingsFromBE.earliest_start),
            endLocationOption: "dontUse",
            endLocation: null,
            endTime: null
          };
          if (vehiclesSettingsFromBE.latest_end) {
            theVehicleSettings.endTime = secondsConvert(
              vehiclesSettingsFromBE.latest_end
            );
          }
          if (vehiclesSettingsFromBE.end_address) {
            if (
              vehiclesSettingsFromBE.end_address.formatted_address ===
              vehiclesSettingsFromBE.start_address.formatted_address
            ) {
              theVehicleSettings.endLocationOption = "returnToStart";
            } else {
              theVehicleSettings.endLocationOption = "otherLocation";
              theVehicleSettings.endLocation =
                vehiclesSettingsFromBE.end_address;
            }
          }
          setVehicleSettings(theVehicleSettings);
          const theRoutingSettings = {
            algorithm: routingSettingsFromBE.algorithm,
            defaultDuration: routingSettingsFromBE.default_duration,
            defaultUnits: routingSettingsFromBE.default_load,
            allowLateStops: routingSettingsFromBE.allow_late_stops || false,
            allowLateStopsMinute: routingSettingsFromBE.allow_late_stops_minute,
            allowBackhaul: routingSettingsFromBE.allow_backhaul || false,
            backhaulLoadDurationPerStop:
              routingSettingsFromBE.backhaul_load_duration_per_stop,
            fixedDepartureTime:
              routingSettingsFromBE.fixed_departure_time || false
          };
          setRoutingSettings(theRoutingSettings);
        })
        .catch(error => {
          enqueueSnackbar(
            "Sorry, something went wrong during loading the data."
          );
          history.goBack();
          console.log(error);
        })
        .finally(() => {
          setLoadingSettings(false);
        });
    }
  }, [enqueueSnackbar, history, open]);

  return (
    <Drawer
      disableEscapeKeyDown
      open={open}
      id={`routeSetupDialog`}
      onClose={warnClose}
      className={classes.root}
      classes={{
        paper: classes.paper
      }}
      anchor="left"
    >
      <Box
        className={clsx(classes.contentWrapper)}
        style={{ paddingLeft: drawerOpen ? 280 : 56 }}
      >
        <Box
          className={classes.header}
          display="flex"
          justifyContent="space-between"
          px={3}
          py={2.5}
        >
          <Box alignSelf="center" alignItems="center" display="flex" pr={2}>
            <IconButton
              onClick={() => warnClose()}
              className={classes.closeButton}
            >
              <CloseIcon />
            </IconButton>
            <TitleWithEducationalMaterials
              title={"Route setup"}
              configId={"route_setup"}
              sectionName={"route setup"}
              articleButtonText="Learn how to setup your routes"
              showArticle
              showDivider
            />
          </Box>
        </Box>
        <Box
          style={{
            maxHeight: "calc(100% - 82px)",
            overflow: "auto",
            position: "relative"
          }}
        >
          <Box px={13} py={3}>
            <DefaultVehicleSettingsCard
              setSaveDisabled={setSaveDisabled}
              vehicleSettings={vehicleSettings}
              loadingSettings={loadingSettings}
            />
            {/*
            <DefaultRouteOptimizationSettingsCard
              setSaveDisabled={setSaveDisabled}
              routingSettings={routingSettings}
              loadingSettings={loadingSettings}
            />
            */}
          </Box>
        </Box>
      </Box>
      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => {
          setChangesDialog({ visible: false, action: () => {} });
        }}
        action={() => {
          changesDialog.action();
          setSaveDisabled(true);
        }}
      />
    </Drawer>
  );
}

const mapStateToProps = state => ({
  drawerOpen: state.account.accountDrawerOpen
});

export default connect(mapStateToProps)(RouteSetupDialog);
